import React from 'react';
import { Box, Typography, Button, alpha, useTheme } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedProps {
    /** Optional message override. */
    message?: string;
    /** Hide the "Go Back" button (e.g. when used as an inline component). */
    hideBack?: boolean;
}

/**
 * AccessDenied — shown when the current user's role is insufficient to access
 * a route or component. Used by RoleGuard as the default fallback.
 */
const AccessDenied: React.FC<AccessDeniedProps> = ({
    message = 'You do not have permission to view this page.',
    hideBack = false,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 2,
                textAlign: 'center',
                p: 4,
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                }}
            >
                <LockIcon sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} color="text.primary">
                Access Denied
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                {message}
            </Typography>

            {!hideBack && (
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </Button>
            )}
        </Box>
    );
};

export default AccessDenied;
